import { Status, Address, EnumObjectValue, enumObject } from '@app/base';
import {
  AddressNullable,
  CareHomeDashboardPatientSummaryFragment,
  PatientContactsFragment,
  PatientDetailsFragment,
  PatientFragment,
  PatientLookupFragment,
  PatientNextOfKinFragment,
} from './graphql';

export function isInactivePatient(patient: Patient | PatientLookup) {
  return patient.status === Status.INACTIVE;
}

export const PatientTitle = enumObject('PatientTitle', ['MR', 'MRS', 'MISS', 'MS', 'DR', 'PROF'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PatientTitle = EnumObjectValue<typeof PatientTitle>;

export const PatientSex = enumObject('Sex', ['MALE', 'FEMALE', 'OTHER', 'PREFER_NOT_TO_SAY'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PatientSex = EnumObjectValue<typeof PatientSex>;

export const NextOfKinRelationship = enumObject('NextOfKinRelationship', [
  'SPOUSE',
  'PARTNER',
  'PARENT',
  'SIBLING',
  'CHILD',
  'FRIEND',
  'CARER',
  'OTHER',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type NextOfKinRelationship = EnumObjectValue<typeof NextOfKinRelationship>;

export const PatientFundingType = enumObject('PatientFundingType', ['PRIVATE', 'NHS', 'CORPORATE'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PatientFundingType = EnumObjectValue<typeof PatientFundingType>;

export const PatientDomiciliaryReason = enumObject('PatientDomiciliaryReason', [
  'HOUSEBOUND',
  'CARE_HOME_RESIDENT',
  'PHYSICAL_DISABILITY',
  'OTHER',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PatientDomiciliaryReason = EnumObjectValue<typeof PatientDomiciliaryReason>;

export interface PatientGeneralPractitionerInput {
  gpName: string | null;
  practiceName: string | null;
  practiceAddress?: AddressNullable | null;
  practiceEmail: string | null;
  practicePhone: string | null;
}

export interface UpdatePatientRequest {
  id: string;
  title: PatientTitle | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: PatientSex;
  careHomeId?: string | null;
  address: Address;
  contacts: PatientContacts;
  nextOfKinContacts: PatientNextOfKin[];
  niNumber: string | null;
  nhsNumber: string | null;
  gosNote: string | null;
  fundingType: PatientFundingType | null;
  domiciliaryReason: PatientDomiciliaryReason | null;
  generalPractitioner?: PatientGeneralPractitionerInput | null;
}

export interface CreatePatientRequest {
  title: PatientTitle | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: PatientSex;
  careHomeId?: string | null;
  address: Address;
  contacts: PatientContacts;
  nextOfKinContacts: PatientNextOfKin[];
  niNumber: string | null;
  nhsNumber: string | null;
  gosNote: string | null;
  fundingType: PatientFundingType | null;
  domiciliaryReason: PatientDomiciliaryReason | null;
  generalPractitioner?: PatientGeneralPractitionerInput | null;
}

export interface InvitePatientRequest {
  id: string;
  email: string;
}

export interface UpdatePatientEmailRequest {
  id: string;
  email: string;
}

export interface UpdatePatientStatusRequest {
  id: string;
  status: Status;
}

export type PatientLookup = PatientLookupFragment;
export type PatientListItem = PatientFragment;
export type Patient = PatientDetailsFragment;
export type CareHomeDashboardPatientSummary = CareHomeDashboardPatientSummaryFragment;
export type PatientContacts = PatientContactsFragment;
export type PatientNextOfKin = PatientNextOfKinFragment;
